import {useQuery} from "@apollo/client";
import {useEffect} from "react";
import {useDebounce} from "use-debounce";

import {Checkbox} from "@/components/checkbox/Checkbox.tsx";
import {MatchModeToggle} from "@/components/matchModeToggle/MatchModeToggle.tsx";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {SearchInput} from "@/components/searchInput/SearchInput.tsx";

import {
  FilterDataResults,
  FilterSets,
  getMatchModeKey,
  GQLFilterCategory,
  MatchMode,
  MAX_GQL_RECORDS,
} from "../FilterDropdown.ts";

import styles from "../FilterDropdown.module.scss";

interface GraphqlOptionsProps {
  category: GQLFilterCategory;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  localFilterSets: FilterSets<GQLFilterCategory>;
  totalOptions: number | null;
  setTotalOptions: (total: number | null) => void;
  onToggleOption: (categoryId: string, optionValue: string) => void;
  onMatchModeChange: (categoryId: string, mode: MatchMode) => void;
}

export const GraphqlOptions = ({
  category,
  searchQuery,
  onSearchChange,
  localFilterSets,
  totalOptions,
  setTotalOptions,
  onToggleOption,
  onMatchModeChange,
}: GraphqlOptionsProps) => {
  const [searchDebounced] = useDebounce(searchQuery, 300);
  const {data, loading, previousData} = useQuery<FilterDataResults>(category.graphql, {
    variables: {
      search: searchDebounced,
      limit: MAX_GQL_RECORDS,
    },
  });

  const displayData = data || previousData;

  // Update total options whenever the fetched total changes so newly created records reflect in the count
  useEffect(() => {
    if (
      searchDebounced === "" &&
      displayData?.values?.total !== undefined &&
      displayData.values.total !== totalOptions
    ) {
      setTotalOptions(displayData.values.total);
    }
  }, [displayData, searchDebounced, totalOptions, setTotalOptions]);

  if (loading && !displayData) {
    return (
      <div className={styles.rightPane}>
        <LoadingContainer text="Loading options…" />
      </div>
    );
  }

  if (!displayData) {
    return null;
  }

  const matchModeKey = getMatchModeKey(category.key);
  const currentMatchMode =
    ((localFilterSets as Record<string, unknown>)[matchModeKey] as MatchMode) ?? category.matchMode?.default ?? "OR";

  return (
    <div className={styles.rightPane}>
      <div className={styles.searchContainer}>
        <SearchInput className={styles.searchInput} value={searchQuery} onChange={onSearchChange} />
      </div>

      {category.matchMode?.enabled && (
        <MatchModeToggle
          value={currentMatchMode}
          onChange={(mode) => onMatchModeChange(category.key, mode)}
          variant="compact"
          className={styles.matchRow}
        />
      )}

      <div className={styles.optionsList}>
        {displayData.values.items.map((opt) => (
          <Checkbox
            key={opt.key}
            id={opt.key}
            checked={((localFilterSets[category.key] as string[]) || []).includes(opt.key.toString())}
            onChange={() => onToggleOption(category.key, opt.key.toString())}
            className={"text-break"}
            label={opt.display}
          />
        ))}
        {totalOptions && displayData.values.items.length < totalOptions && (
          <div>...and {totalOptions - displayData.values.items.length} more. Use search to narrow down.</div>
        )}
      </div>
    </div>
  );
};
